import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "../context/WalletContext.jsx";
import { shortAddress } from "../utils/format.js";
import config from "../config/index.js";
import WalletConnectModal from "./WalletConnectModal.jsx";

/**
 * Wallet connect button. Three visual states:
 *   - Connected & on the right chain: pill with truncated address
 *   - Connected but wrong chain: red "Switch network" CTA
 *   - Disconnected: yellow "Connect Wallet" CTA that opens a wallet
 *     selection modal (MetaMask / WalletConnect / Coinbase / Other).
 *     If the user rejects the wallet prompt, the modal switches to a
 *     rejection view with retry / close / "Back to Home" actions.
 */
export default function WalletConnect() {
    const {
        address,
        ensName,
        chainId,
        wrongChain,
        walletKind,
        connect,
        switchNetwork,
        disconnect,
        clearError,
        connectModalOpen,
        requestConnect,
        closeConnectModal,
        consumePendingDestination
    } = useWallet();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [view, setView] = useState("select");
    const [rejectionMessage, setRejectionMessage] = useState(null);

    async function handleSelectWallet(eip1193Provider, kind = null) {
        setBusy(true);
        const result = await connect(eip1193Provider, kind);
        setBusy(false);
        if (result?.ok) {
            const destination = consumePendingDestination();
            handleClose();
            navigate(destination || "/dashboard");
            return;
        }
        setRejectionMessage(result?.error || "Connection failed.");
        setView("rejected");
    }

    function handleClose() {
        setView("select");
        setRejectionMessage(null);
        clearError();
        closeConnectModal();
    }

    function handleDisconnect() {
        disconnect();
        navigate("/");
    }

    function handleRetry() {
        setRejectionMessage(null);
        setView("select");
        clearError();
    }

    if (address && wrongChain) {
        return (
            <div className="flex flex-col items-end gap-1">
                <button className="btn-danger" onClick={switchNetwork}>
                    Switch to {config.network.name}
                </button>
                <span className="text-xs text-vb-muted">Currently on chain {chainId}</span>
            </div>
        );
    }

    if (address) {
        return (
            <ConnectedDropdown
                address={address}
                ensName={ensName}
                walletKind={walletKind}
                onDisconnect={handleDisconnect}
            />
        );
    }

    return (
        <>
            <button
                className="inline-flex items-center justify-center gap-2 rounded-[14px] bg-[#FBC206] px-4 sm:px-6 py-2 font-manrope text-sm font-bold text-[#1D3E29] shadow-sm hover:-translate-y-0.5 hover:shadow-lg transition-all"
                onClick={() => requestConnect(null)}
            >
                <span className="hidden sm:inline">Connect Wallet</span>
                <span className="sm:hidden">Connect</span>
                <svg
                    className="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polyline points="7 4 13 10 7 16" />
                </svg>
            </button>
            <WalletConnectModal
                open={connectModalOpen}
                busy={busy}
                view={view}
                rejectionMessage={rejectionMessage}
                onClose={handleClose}
                onRetry={handleRetry}
                onSelectWallet={handleSelectWallet}
            />
        </>
    );
}

/**
 * Connected-state header pill + dropdown. Replaces the inert address
 * span the Layout chrome used to render: clicking the pill opens a
 * menu with Copy address / View on explorer / Disconnect. The Layout's
 * decorative down-chevron was removed in favour of this since it
 * suggested a dropdown that never existed.
 *
 * Outside-click + Escape close the menu, mirroring the
 * Sort/Filters/Notification popovers used elsewhere in the app so the
 * chrome behaves consistently.
 */
function ConnectedDropdown({ address, ensName, walletKind, onDisconnect }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const wrapperRef = useRef(null);
    const display = ensName || shortAddress(address);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (event) => {
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(address);
            setCopied(true);
            // Brief visual confirmation, then revert. Closing the menu
            // immediately would hide the feedback before the user
            // sees it; we let them dismiss it themselves.
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard API blocked (older browsers, insecure context).
            // Silent — the user can still see / copy the address from
            // the menu header.
        }
    }

    const explorerBase = config.network.blockExplorerUrl?.replace(/\/+$/, "");
    const explorerHref = explorerBase ? `${explorerBase}/address/${address}` : null;

    return (
        <div ref={wrapperRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Wallet menu for ${display}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#10281b] px-3 py-1.5 ring-1 ring-[#FBC206]/30 hover:bg-[#162e1f] transition-colors md:px-4"
            >
                <span className="hidden text-xs font-medium text-[#e6c985] md:inline">
                    {display}
                </span>
                <WalletKindBadge kind={walletKind} />
                <svg
                    className={`h-3 w-3 text-[#e6c985] transition-transform ${open ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polyline points="4 7 10 13 16 7" />
                </svg>
            </button>

            {open ? (
                <div
                    role="menu"
                    aria-label="Wallet actions"
                    className="absolute right-0 top-full z-[60] mt-2 w-64 overflow-hidden rounded-[12px] bg-white ring-1 ring-[#bfb67b] shadow-xl"
                >
                    <div className="border-b border-[#1D3E29]/10 bg-[#1D3E29] px-4 py-2.5">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            Connected Wallet
                        </div>
                        <div className="mt-0.5 font-mono text-[11px] text-white break-all">
                            {address}
                        </div>
                        {ensName ? (
                            <div className="mt-0.5 text-[10px] text-[#e6c985]/80">{ensName}</div>
                        ) : null}
                    </div>
                    <ul className="p-1">
                        <li>
                            <button
                                type="button"
                                role="menuitem"
                                onClick={handleCopy}
                                className="flex w-full items-center gap-2 rounded-[8px] px-3 py-2 text-[11.5px] font-semibold text-[#1D3E29] hover:bg-[#1D3E29]/5"
                            >
                                <CopyIcon />
                                {copied ? "Copied!" : "Copy address"}
                            </button>
                        </li>
                        {explorerHref ? (
                            <li>
                                <a
                                    href={explorerHref}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    role="menuitem"
                                    onClick={() => setOpen(false)}
                                    className="flex w-full items-center gap-2 rounded-[8px] px-3 py-2 text-[11.5px] font-semibold text-[#1D3E29] hover:bg-[#1D3E29]/5"
                                >
                                    <ExternalIcon />
                                    View on {explorerLabel(explorerBase)}
                                </a>
                            </li>
                        ) : null}
                        <li className="mt-1 border-t border-[#1D3E29]/10 pt-1">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setOpen(false);
                                    onDisconnect();
                                }}
                                className="flex w-full items-center gap-2 rounded-[8px] px-3 py-2 text-[11.5px] font-semibold text-[#C93030] hover:bg-[#C93030]/10"
                            >
                                <PowerIcon />
                                Disconnect
                            </button>
                        </li>
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

function CopyIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="6" y="6" width="10" height="11" rx="1.5" />
            <path d="M13 6V4.5A1.5 1.5 0 0 0 11.5 3H5.5A1.5 1.5 0 0 0 4 4.5v10A1.5 1.5 0 0 0 5.5 16H6" />
        </svg>
    );
}

function ExternalIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 3h6v6" />
            <path d="M17 3 10 10" />
            <path d="M17 12v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4" />
        </svg>
    );
}

function PowerIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10 3v8" />
            <path d="M14.5 5.5a6 6 0 1 1-9 0" />
        </svg>
    );
}

// Recognise common block-explorer brands by the second-level segment of
// the hostname. Without this, a testnet host like `sepolia.basescan.org`
// would render as "View on Sepolia" — the previous first-segment logic
// picked up the network qualifier instead of the actual explorer name.
const KNOWN_EXPLORERS = {
    basescan: "BaseScan",
    etherscan: "Etherscan",
    polygonscan: "PolygonScan",
    arbiscan: "Arbiscan",
    bscscan: "BscScan",
    ftmscan: "FtmScan",
    snowtrace: "SnowTrace",
    blockscout: "Blockscout"
};

function explorerLabel(url) {
    if (!url) return "Explorer";
    try {
        const host = new URL(url).hostname.replace(/^www\./, "");
        const parts = host.split(".");
        for (const segment of parts) {
            if (KNOWN_EXPLORERS[segment]) return KNOWN_EXPLORERS[segment];
        }
        // Unknown chain — fall back to the second-level segment
        // capitalised (e.g. `myscan.io` → "Myscan").
        const sld = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
        return sld ? sld.charAt(0).toUpperCase() + sld.slice(1) : "Explorer";
    } catch {
        return "Explorer";
    }
}

// Per-wallet badge config. Two render modes:
//   - `iconSrc`  → monochrome SVG masked with currentColor (gold).
//                  Used for the stroke-style logos that alpha-mask
//                  cleanly (MetaMask, WalletConnect).
//   - `imageSrc` → full-colour <img>. Used for the filled-shape brand
//                  SVGs (coinBase, rabby, safe_wallet) which can't be
//                  recoloured without losing recognisable shape.
//                  Their natural brand colours are kept; the cream
//                  badge background makes them sit cleanly in the
//                  header chrome.
const BADGE_BG = "#f6f6ec";
const BADGE_FG = "#b98c23";

const WALLET_BADGES = {
    metamask: { iconSrc: "/images/metaMaskIcon.svg" },
    coinbase: { imageSrc: "/images/coinBase.svg" },
    walletconnect: { iconSrc: "/images/walletConnect.svg" },
    rabby: { imageSrc: "/images/rabby.svg" },
    safe: { imageSrc: "/images/safe_wallet.svg" }
};

function WalletKindBadge({ kind }) {
    if (!kind) return null;
    const cfg = WALLET_BADGES[kind] || {};
    return (
        <span
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-[#bfb67b]/30 md:h-7 md:w-7"
            style={{ backgroundColor: BADGE_BG, color: BADGE_FG }}
            aria-hidden="true"
            title={kindLabel(kind)}
        >
            {cfg.imageSrc ? (
                <img
                    src={cfg.imageSrc}
                    alt=""
                    className="h-3.5 w-3.5 object-contain"
                    loading="lazy"
                />
            ) : cfg.iconSrc ? (
                <MaskCircle src={cfg.iconSrc} />
            ) : (
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                    <circle cx="4" cy="10" r="1.6" />
                    <circle cx="10" cy="10" r="1.6" />
                    <circle cx="16" cy="10" r="1.6" />
                </svg>
            )}
        </span>
    );
}

function MaskCircle({ src }) {
    return (
        <span
            aria-hidden="true"
            className="inline-block h-3.5 w-3.5 shrink-0"
            style={{
                backgroundColor: "currentColor",
                WebkitMaskImage: `url(${src})`,
                maskImage: `url(${src})`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain"
            }}
        />
    );
}

function kindLabel(kind) {
    if (kind === "metamask") return "MetaMask";
    if (kind === "coinbase") return "Coinbase Wallet";
    if (kind === "walletconnect") return "Wallet Connect";
    if (kind === "rabby") return "Rabby Wallet";
    if (kind === "safe") return "Safe";
    return "Connected wallet";
}
